import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import { API_URL } from "config";
import FlagLoader from "layouts/flagloader";
import { useAuth } from "../../../AuthContext";

const parseStoredPermissions = () => {
  try {
    const stored = localStorage.getItem("permissions");
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const EditBranch = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();
  const [branch, setBranch] = useState({ name: "", address: "", status: "active" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const grantedPermissions = useMemo(
    () => Array.from(new Set([...permissions, ...userPermission, ...parseStoredPermissions()])),
    [permissions, userPermission]
  );

  const hasBranchPermission = (...requiredPermissions) => {
    if (grantedPermissions.includes("access.system.full_access")) return true;
    return requiredPermissions
      .flat()
      .filter(Boolean)
      .some((permission) => grantedPermissions.includes(permission));
  };

  const canViewPage = hasBranchPermission("branch.pages.edit.view");
  const canSubmit =
    hasBranchPermission("branch.actions.update") &&
    hasBranchPermission("branch.actions.submit_update");
  const canViewField = (field) => hasBranchPermission(`branch.fields.${field}.view`);

  useEffect(() => {
    if (!isAuthReady) return;

    if (!canViewPage) {
      setPageLoading(false);
      return;
    }

    const fetchBranch = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await fetch(`${API_URL}/branches/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || "Error loading branch");
        }

        const data = await response.json();
        setBranch({
          name: data.data.name || "",
          address: data.data.address || "",
          status: data.data.status === 1 ? "active" : "inactive",
        });
      } catch (error) {
        setSnackbar({ open: true, message: error.message || "Error loading branch", color: "error" });
      } finally {
        setPageLoading(false);
      }
    };

    fetchBranch();
  }, [canViewPage, id, isAuthReady]);

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!canSubmit) {
      setSnackbar({ open: true, message: "You do not have permission to update branches", color: "error" });
      return;
    }

    if (!branch.name.trim() || !branch.address.trim()) {
      setSnackbar({
        open: true,
        message: "Branch Name and Location are required.",
        color: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_URL}/branches/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: branch.name,
          address: branch.address,
          status: branch.status === "active" ? 1 : 0,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Update failed");
      }

      setSnackbar({ open: true, message: "Branch updated successfully", color: "success" });
      setTimeout(() => navigate("/branches"), 1500);
    } catch (error) {
      setSnackbar({ open: true, message: error.message || "Update failed", color: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthReady || pageLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="60vh">
          <FlagLoader />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Edit Branch
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to edit branches.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Branch"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar({ ...snackbar, open: false })}
      />

      <SoftBox p={3}>
        <SoftTypography variant="h5" mb={2}>
          Edit Branch
        </SoftTypography>
        <form onSubmit={handleUpdate}>
          {canViewField("name") && (
            <SoftBox mb={2}>
              <SoftInput
                placeholder="Branch Name"
                value={branch.name}
                onChange={(e) => setBranch({ ...branch, name: e.target.value })}
              />
            </SoftBox>
          )}
          {canViewField("address") && (
            <SoftBox mb={2}>
              <SoftInput
                placeholder="Location"
                value={branch.address}
                onChange={(e) => setBranch({ ...branch, address: e.target.value })}
              />
            </SoftBox>
          )}
          {canViewField("status") && (
            <SoftBox display="flex" alignItems="center" mb={2}>
              <Switch
                checked={branch.status === "active"}
                onChange={() =>
                  setBranch({
                    ...branch,
                    status: branch.status === "active" ? "inactive" : "active",
                  })
                }
              />
              <SoftTypography variant="body2" ml={1}>
                {branch.status === "active" ? "Active" : "Inactive"}
              </SoftTypography>
            </SoftBox>
          )}
          {canSubmit && (
            <SoftButton
              type="submit"
              color="info"
              disabled={loading}
              endIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? "Processing..." : "Update Branch"}
            </SoftButton>
          )}
        </form>
      </SoftBox>
    </DashboardLayout>
  );
};

export default EditBranch;
